import { useEffect, useState } from 'react';
import type { ComponentType } from 'react';

import { getUserWidgetContent } from 'api/generated/user-widgets/user-widgets';

interface BaseWidgetProps {
  loading?: boolean;
  error?: unknown;
  userId: string;
  widgetId: string | number;
  removeWidget: (widgetId: string | number) => void;
}

interface InjectedHocProps {
  userId: string;
  widgetId: string | number;
  removeWidget: (widgetId: string | number) => void;
}

const withWidgetData =
  <P extends BaseWidgetProps>(WrappedComponent: ComponentType<P>) =>
  ({
    userId,
    widgetId,
    removeWidget,
    ...props
  }: InjectedHocProps & Omit<P, keyof BaseWidgetProps>): React.JSX.Element => {
    const [loading, setLoading] = useState<boolean>(true);
    const [widgetData, setWidgetData] = useState<Record<string, unknown> | null>(null);
    const [error, setError] = useState<unknown>(null);

    useEffect(() => {
      const fetchWidgetData = async (): Promise<void> => {
        try {
          const data = (await getUserWidgetContent(
            userId,
            Number(widgetId),
          )) as unknown as { widget: { content: Record<string, unknown> } };
          const contentData = data.widget.content;

          setWidgetData(contentData);
        } catch (error) {
          setError(error);
          console.error('Error fetching widget data:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchWidgetData();
    }, [userId, widgetId]);

    return (
      <WrappedComponent
        {...({
          loading,
          error,
          userId,
          widgetId,
          removeWidget,
          ...props,
          ...widgetData,
        } as unknown as P)}
      />
    );
  };

export default withWidgetData;
